import React, { createContext, useContext, useState, ReactNode } from 'react';
import { useClarity } from '../hooks/useClarity';
import { Cart, CartItem, DigitalEmployee } from '../types';

interface CartContextType {
  cart: Cart;
  addToCart: (employee: DigitalEmployee, hours: number) => void;
  removeFromCart: (employeeId: string) => void;
  updateHours: (employeeId: string, hours: number) => void;
  clearCart: () => void;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

interface CartProviderProps {
  children: ReactNode;
}

export const CartProvider: React.FC<CartProviderProps> = ({ children }) => {
  const { trackEvent } = useClarity();
  const [cart, setCart] = useState<Cart>({
    items: [],
    total: 0
  });

  const calculateTotal = (items: CartItem[]): number => {
    // Using fixed price of $20 per seat per month
    return items.reduce((sum, item) => sum + (20 * item.hours), 0);
  };

  const addToCart = (employee: DigitalEmployee, hours: number) => {
    setCart(prevCart => {
      // Check if employee is already in cart
      const existingItemIndex = prevCart.items.findIndex(
        item => item.employee.id === employee.id
      );

      let newItems: CartItem[];
      let isNewItem = false;

      if (existingItemIndex >= 0) {
        // Update existing item
        newItems = [...prevCart.items];
        newItems[existingItemIndex] = {
          ...newItems[existingItemIndex],
          hours: newItems[existingItemIndex].hours + hours
        };
      } else {
        // Add new item
        newItems = [...prevCart.items, { employee, hours }];
        isNewItem = true;
      }

      // Track the add to cart event
      if (isNewItem) {
        trackEvent('add_to_cart', {
          employee_id: employee.id,
          employee_name: employee.name,
          hours: hours,
          price: 20 * hours // $20 per seat per month
        });
      } else {
        trackEvent('update_cart_item', {
          employee_id: employee.id,
          employee_name: employee.name,
          hours: hours,
          price: 20 * hours, // $20 per seat per month
          action: 'add_hours'
        });
      }

      return {
        items: newItems,
        total: calculateTotal(newItems)
      };
    });
  };

  const removeFromCart = (employeeId: string) => {
    setCart(prevCart => {
      const itemToRemove = prevCart.items.find(item => item.employee.id === employeeId);
      const newItems = prevCart.items.filter(item => item.employee.id !== employeeId);

      // Track the remove from cart event
      if (itemToRemove) {
        trackEvent('remove_from_cart', {
          employee_id: employeeId,
          employee_name: itemToRemove.employee.name,
          hours: itemToRemove.hours,
          price: 20 * itemToRemove.hours // $20 per seat per month
        });
      }

      return {
        items: newItems,
        total: calculateTotal(newItems)
      };
    });
  };

  const updateHours = (employeeId: string, hours: number) => {
    setCart(prevCart => {
      const itemToUpdate = prevCart.items.find(item => item.employee.id === employeeId);
      const newItems = prevCart.items.map(item =>
        item.employee.id === employeeId
          ? { ...item, hours }
          : item
      );

      // Track the update hours event
      if (itemToUpdate) {
        trackEvent('update_cart_item', {
          employee_id: employeeId,
          employee_name: itemToUpdate.employee.name,
          old_hours: itemToUpdate.hours,
          new_hours: hours,
          price_change: (hours - itemToUpdate.hours) * 20, // $20 per seat per month
          action: 'update_hours'
        });
      }

      return {
        items: newItems,
        total: calculateTotal(newItems)
      };
    });
  };

  const clearCart = () => {
    // Track the clear cart event
    if (cart.items.length > 0) {
      trackEvent('clear_cart', {
        item_count: cart.items.length,
        total_value: cart.total
      });
    }

    setCart({
      items: [],
      total: 0
    });
  };

  return (
    <CartContext.Provider value={{ cart, addToCart, removeFromCart, updateHours, clearCart }}>
      {children}
    </CartContext.Provider>
  );
};
